import { API, deleteRequest, getRequest, patchRequest, postRequest } from '@/api/api';
import { generateSearchParams } from '@/utils/generate-search-params';

import type {
  CreateDocumentsFolderFormValues,
  DocumentPermission,
  DocumentsCursorFilters,
  DocumentsCursorPage,
  DocumentsCursorPageDto,
  DocumentsList,
  DocumentsListDto,
  DocumentView,
  DocumentViewDto,
  RecentDocumentsType,
  RenameDucumentFormValues,
  SingleDocument,
  SingleDocumentDto,
} from './documents.dto';
import { mapDocumentView, mapSingleDocument } from './documents.mappers';

export const getDocumentsToken = async () => {
  return await getRequest(API.DOCUMENTS, 'documents/csrf');
};

/**
 * One step of the cursor stream.
 *
 * Three deliberate absences, each of which the service reads as an instruction:
 * no `page` is what puts it in stream mode at all (`GET /documents` streams when
 * the page is omitted and counts when it is not); no `order_by` because a cursor
 * sent with one is a 400; and no `with_count`, because the count is the half of
 * the request worth skipping and an endless row never shows a total anyway.
 *
 * The first step sends no `cursor` either — the stream starts at the beginning.
 */
export const getDocumentsByCursor = async (
  limit: number,
  cursor: string,
  parentId: null | string,
  filters: DocumentsCursorFilters,
): Promise<DocumentsCursorPage> => {
  const searchParams = generateSearchParams({
    ...(parentId !== null && { parent_id: parentId }),
    limit,
    ...(cursor && { cursor }),
    search: filters.search || undefined,
    type: filters.type,
  });

  const { data } = await getRequest<DocumentsCursorPageDto>(
    API.DOCUMENTS,
    `documents?${searchParams}`,
  );
  return {
    items: data.items.map(mapSingleDocument),
    // Absent means false and absent means "no more", which is the same answer
    // twice — but only `has_more` is the one to trust: a service that ran out of
    // rows exactly on the limit still has nothing to hand over.
    hasMore: data.has_more ?? false,
    nextCursor: data.next_cursor ?? null,
  };
};

/**
 * What this person opened most recently in this service, newest first.
 *
 * Opened, not updated: the list is a join onto the view table, which only
 * `POST /documents/view/:id` ever writes. Reading a document does not put it
 * here — see {@link addDocumentView}.
 *
 * A plain array rather than a page: the service caps the limit at 50 and hands
 * back everything under it, so there is nothing to page through. Asking for more
 * than the cap is clamped silently rather than refused.
 */
export const getRecentDocuments = async (
  type: RecentDocumentsType,
  limit: number,
): Promise<SingleDocument[]> => {
  const searchParams = generateSearchParams({ type, limit });

  const { data } = await getRequest<SingleDocumentDto[]>(
    API.DOCUMENTS,
    `documents/recent?${searchParams}`,
  );
  return (data ?? []).map(mapSingleDocument);
};

export const getAllDocuments = async (): Promise<DocumentsList> => {
  const searchParams = generateSearchParams({
    limit: 9999,
    page: 1,
    search: '',
    order_by: 'date',
    order_direction: 'desc',
  });

  const { data } = await getRequest<DocumentsListDto>(API.DOCUMENTS, `documents?${searchParams}`);
  return {
    count: data.count,
    items: data.items.map(mapSingleDocument),
  };
};

export const getDocumentById = async (id: string): Promise<SingleDocument> => {
  const { data } = await getRequest<SingleDocumentDto>(API.DOCUMENTS, `documents/${id}`);
  return mapSingleDocument(data);
};

export const createDocumentsFolder = async ({
  parentId,
  name,
  color,
}: CreateDocumentsFolderFormValues) => {
  return await postRequest(API.DOCUMENTS, `documents/folder`, {
    parent_id: parentId,
    name,
    color: `${color};${color}33`,
  });
};

export const renameDocument = async ({ id, name }: RenameDucumentFormValues) => {
  return await patchRequest(API.DOCUMENTS, `documents/rename/${id}`, { name });
};

export const changeDocumentColor = async ({ id, color }: { id: string; color: string }) => {
  return await patchRequest(API.DOCUMENTS, `documents/color/${id}`, {
    color: `${color};${color}33`,
  });
};

export const uploadDocumentsFile = async ({
  file,
  parentId,
  name,
  color,
}: {
  parentId?: null | string;
  name?: string;
  file: File;
  color?: null | string;
}) => {
  const formData = new FormData();
  if (parentId) {
    formData.append('parent_id', parentId);
  }
  if (name) {
    formData.append('custom_name', name);
  }
  if (color) {
    formData.append('color', `${color};${color}33`);
  }
  formData.append('file', file);
  return await postRequest(API.DOCUMENTS, `documents/upload`, formData);
};

export const uploadFinancialDocumentsFile = async ({ file }: { file: File }) => {
  const formData = new FormData();
  formData.append('file', file);
  return await postRequest(API.DOCUMENTS, `documents/upload/financial`, formData);
};

export const deleteDocument = async (id: string) => {
  return await deleteRequest(API.DOCUMENTS, `documents/${id}`);
};

export const shareDocument = async ({ id, shareWith }: { id: string; shareWith: string[] }) => {
  return await patchRequest(API.DOCUMENTS, `documents/share/${id}`, { share_with: shareWith });
};

export const unshareDocument = async ({
  id,
  unshareWith,
}: {
  id: string;
  unshareWith: string[];
}) => {
  return await patchRequest(API.DOCUMENTS, `documents/unshare/${id}`, {
    unshare_with: unshareWith,
  });
};

export const changeDocumentPermissions = async ({
  id,
  permissions,
}: {
  id: string;
  permissions: DocumentPermission[];
}) => {
  return await postRequest(API.DOCUMENTS, `documents/permissions/${id}`, { permissions });
};

export const getDocumentViews = async (id: string): Promise<DocumentView[]> => {
  const { data } = await getRequest<DocumentViewDto[]>(API.DOCUMENTS, `documents/view/${id}`);
  return (data ?? []).map(mapDocumentView);
};

export const addDocumentView = async (id: string): Promise<void> => {
  await postRequest(API.DOCUMENTS, `documents/view/${id}`, {});
};
